import { useEffect, useState } from 'react';
import { DISCIPLINE_LABEL } from '@flm9/i18n/labels';
import type { DrillRow, ScoringMode } from '../lib/api';
import { formatMagazines } from '../lib/magazines';
import { errorText } from '../lib/errorText';

interface Props {
  drill: DrillRow;
  mode: ScoringMode;
  distanceCm: number;
  /** Available disciplines (pistol / pistol_carbine / rifle). */
  disciplines: Array<{ id: string; code: string }>;
  /** Loading the disciplines failed (null = loading or loaded). */
  disciplinesError?: string | null;
  onRetryDisciplines?: () => void;
  onConfirm: (par: {
    parSecondsOverride: number | null;
    parModeOverride: 'soft' | 'hard' | 'hard_stop' | null;
    /** Set only for "Kein PAR": the round runs without any PAR. */
    parDisabled?: true;
    disciplineId: string;
  }) => Promise<void> | void;
  onBack: () => void;
}

/**
 * Step 4 of the round-setup flow: referee picks the PAR time + mode.
 * Defaults from the drill's recommendation; the referee can deviate
 * per round. Override is sent to the server as NULL for fields the
 * referee left unchanged so the audit trail records "accepted the
 * recommendation" vs. "deviated".
 *
 * Quick-pick chips cover the standard practice par times. "Andere…"
 * opens a numeric input (0,1–300 s, decimal-comma supported). Mode
 * toggle picks `soft` (informational) vs `hard` (auto-DQ on exceed).
 *
 * "Kein PAR" lets the referee start without a PAR even when the
 * drill recommends one.
 */
const QUICK_PICKS_S = [3, 5, 10, 15, 20, 30, 45, 60];

function fmtSeconds(s: number): string {
  return `${s.toLocaleString('de-AT', { maximumFractionDigits: 2 })} s`;
}

function parseCustomSeconds(raw: string): number | null {
  const cleaned = raw.replace(',', '.').trim();
  if (cleaned === '') return null;
  const n = Number.parseFloat(cleaned);
  if (!Number.isFinite(n) || n <= 0 || n > 300) return null;
  // 5.2 numeric — two decimal places max.
  return Math.round(n * 100) / 100;
}

function approxEqual(a: number | null, b: number | null): boolean {
  if (a == null || b == null) return a === b;
  return Math.abs(a - b) < 0.005;
}

export function ParPicker({
  drill,
  mode,
  distanceCm,
  disciplines,
  disciplinesError = null,
  onRetryDisciplines,
  onConfirm,
  onBack,
}: Props) {
  const recommendedS = drill.drill.parSeconds == null ? null : Number(drill.drill.parSeconds);
  const recommendedMode = drill.drill.parMode;
  // Pre-fill discipline ONLY from the drill's explicit default hint. When the
  // drill has no default we deliberately leave this unset so the referee is
  // asked to pick one (rather than silently recording the first discipline) —
  // "Starten" stays disabled until they do.
  const [pickedDisciplineId, setPickedDisciplineId] = useState<string>(
    () => drill.drill.defaultDisciplineId ?? '',
  );

  const initialMatchesQuickPick =
    recommendedS != null && QUICK_PICKS_S.some((p) => approxEqual(p, recommendedS));

  type Selection = { kind: 'quick'; s: number } | { kind: 'custom' } | { kind: 'none' };
  const [selection, setSelection] = useState<Selection>(() => {
    if (recommendedS == null) return { kind: 'none' };
    return initialMatchesQuickPick ? { kind: 'quick', s: recommendedS } : { kind: 'custom' };
  });
  const [customRaw, setCustomRaw] = useState<string>(
    recommendedS != null && !initialMatchesQuickPick
      ? recommendedS.toLocaleString('de-AT', { maximumFractionDigits: 2 })
      : '',
  );
  const [pickedMode, setPickedMode] = useState<'soft' | 'hard' | 'hard_stop'>(recommendedMode);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // App can't tell "still loading" from "loaded, but empty" (both: no
  // list, no error) — after a while stop saying "werden geladen…" and
  // offer a retry instead (REF-17). `waitRound` restarts the wait.
  const [waitRound, setWaitRound] = useState(0);
  const [waitedLong, setWaitedLong] = useState(false);
  useEffect(() => {
    if (disciplines.length > 0) return;
    setWaitedLong(false);
    const t = window.setTimeout(() => setWaitedLong(true), 8_000);
    return () => window.clearTimeout(t);
  }, [disciplines.length, waitRound]);

  const customS = selection.kind === 'custom' ? parseCustomSeconds(customRaw) : null;
  const effectiveS =
    selection.kind === 'quick' ? selection.s : selection.kind === 'custom' ? customS : null;
  const parReady = selection.kind === 'none' || effectiveS != null;
  const canStart = parReady && pickedDisciplineId !== '';

  async function start() {
    if (!canStart) return;
    setBusy(true);
    setError(null);
    try {
      // "Kein PAR" must say so explicitly — an override of NULL means
      // "use the drill's PAR" on the server.
      if (selection.kind === 'none') {
        await onConfirm({
          parSecondsOverride: null,
          parModeOverride: null,
          parDisabled: true,
          disciplineId: pickedDisciplineId,
        });
        return;
      }
      // Send NULL when the referee accepted the recommendation
      // (audit-trail signal that no deviation was made). Otherwise
      // send the chosen value.
      const acceptedSeconds = approxEqual(effectiveS, recommendedS);
      const acceptedMode = pickedMode === recommendedMode;
      await onConfirm({
        parSecondsOverride: acceptedSeconds ? null : effectiveS,
        parModeOverride: acceptedMode ? null : pickedMode,
        disciplineId: pickedDisciplineId,
      });
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  }

  const distanceLabel = `${(distanceCm / 100).toLocaleString('de-AT', { maximumFractionDigits: 2 })} m`;

  return (
    <section className="space-y-4">
      <header className="flex items-center justify-between">
        <button onClick={onBack} className="min-h-12 pr-3 text-sm text-zinc-500 underline">
          ← zurück
        </button>
        <h2 className="text-lg font-bold">PAR wählen</h2>
      </header>

      <p className="text-sm text-zinc-400">
        <span className="font-medium text-zinc-200">{drill.drill.name}</span> ·{' '}
        {mode === 'speed' ? 'Speed' : 'Präzision'} · {drill.drill.shotCount} Schuss
        {formatMagazines(drill.drill.magazines)
          ? ` (${formatMagazines(drill.drill.magazines)})`
          : ''}{' '}
        · {distanceLabel}
      </p>

      {recommendedS != null && (
        <p className="text-xs text-zinc-500">
          Empfehlung: {fmtSeconds(recommendedS)} ·{' '}
          {recommendedMode === 'soft'
            ? 'Weich (nur Markierung)'
            : recommendedMode === 'hard_stop'
              ? 'Hart, Stopp (Auto-DQ, Timer stoppt)'
              : 'Hart, offen (Auto-DQ, läuft weiter)'}
        </p>
      )}

      {error && (
        <div role="alert" className="space-y-3 rounded bg-red-900/30 p-3 text-sm text-red-300">
          <p>Fehler: {error}</p>
          <button
            type="button"
            onClick={() => void start()}
            disabled={busy}
            className="min-h-12 rounded-lg border border-red-400/60 px-4 py-2 font-semibold text-red-100 hover:bg-red-900/40 disabled:opacity-50"
          >
            Erneut versuchen
          </button>
        </div>
      )}

      <div>
        <p className="mb-2 text-sm font-medium text-zinc-300">Disziplin</p>
        {disciplines.length === 0 && disciplinesError ? (
          <div role="alert" className="space-y-2 rounded bg-red-900/30 p-3 text-sm text-red-300">
            <p>Disziplinen konnten nicht geladen werden: {disciplinesError}</p>
            {onRetryDisciplines && (
              <button
                type="button"
                onClick={onRetryDisciplines}
                className="min-h-12 rounded-lg border border-red-400/60 px-4 py-2 font-semibold text-red-100"
              >
                Erneut versuchen
              </button>
            )}
          </div>
        ) : disciplines.length === 0 && waitedLong ? (
          <div role="alert" className="space-y-2 rounded bg-red-900/30 p-3 text-sm text-red-300">
            <p>Keine Disziplinen erhalten.</p>
            {onRetryDisciplines && (
              <button
                type="button"
                onClick={() => {
                  onRetryDisciplines();
                  setWaitRound((n) => n + 1);
                }}
                className="min-h-12 rounded-lg border border-red-400/60 px-4 py-2 font-semibold text-red-100"
              >
                Erneut versuchen
              </button>
            )}
          </div>
        ) : disciplines.length === 0 ? (
          <p className="text-sm text-amber-300">Disziplinen werden geladen…</p>
        ) : (
          <div className="grid grid-cols-3 gap-2">
            {disciplines.map((d) => {
              const active = pickedDisciplineId === d.id;
              return (
                <button
                  key={d.id}
                  type="button"
                  onClick={() => setPickedDisciplineId(d.id)}
                  className={`rounded-lg border px-3 py-3 text-sm font-semibold transition-colors ${
                    active
                      ? 'border-sky-500 bg-sky-700 text-white'
                      : 'border-zinc-700 bg-zinc-900 text-zinc-200 hover:border-zinc-500'
                  }`}
                >
                  {DISCIPLINE_LABEL[d.code] ?? d.code}
                </button>
              );
            })}
          </div>
        )}
        {disciplines.length > 0 && pickedDisciplineId === '' && (
          <p className="mt-2 text-xs text-amber-300">Bitte zuerst eine Disziplin wählen.</p>
        )}
      </div>

      <div>
        <p className="mb-2 text-sm font-medium text-zinc-300">PAR</p>
      </div>

      <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
        {QUICK_PICKS_S.map((s) => {
          const active = selection.kind === 'quick' && approxEqual(selection.s, s);
          return (
            <button
              key={s}
              type="button"
              onClick={() => setSelection({ kind: 'quick', s })}
              className={`rounded-lg border px-3 py-4 text-base font-semibold transition-colors ${
                active
                  ? 'border-emerald-500 bg-emerald-700 text-white'
                  : 'border-zinc-700 bg-zinc-900 text-zinc-200 hover:border-zinc-500'
              }`}
            >
              {fmtSeconds(s)}
            </button>
          );
        })}
        <button
          type="button"
          onClick={() => setSelection({ kind: 'custom' })}
          className={`rounded-lg border px-3 py-4 text-base font-semibold transition-colors ${
            selection.kind === 'custom'
              ? 'border-emerald-500 bg-emerald-700 text-white'
              : 'border-zinc-700 bg-zinc-900 text-zinc-200 hover:border-zinc-500'
          }`}
        >
          Andere…
        </button>
        <button
          type="button"
          onClick={() => setSelection({ kind: 'none' })}
          className={`rounded-lg border px-3 py-4 text-base font-semibold transition-colors ${
            selection.kind === 'none'
              ? 'border-emerald-500 bg-emerald-700 text-white'
              : 'border-zinc-700 bg-zinc-900 text-zinc-200 hover:border-zinc-500'
          }`}
        >
          Kein PAR
        </button>
      </div>

      {selection.kind === 'custom' && (
        <label className="block">
          <span className="text-sm text-zinc-400">PAR in Sekunden</span>
          <input
            type="text"
            inputMode="decimal"
            value={customRaw}
            onChange={(e) => setCustomRaw(e.target.value)}
            placeholder="z. B. 2,5"
            className="mt-1 w-full rounded-lg border border-zinc-700 bg-zinc-900 px-3 py-3 text-lg font-medium tabular-nums text-zinc-100 focus:border-emerald-500 focus:outline-none"
          />
          {customRaw !== '' && customS == null && (
            <span className="mt-1 block text-xs text-amber-300">
              Ungültig — bitte 0,1–300 s, z. B. „2,5".
            </span>
          )}
        </label>
      )}

      {selection.kind !== 'none' && (
        <div>
          <p className="mb-2 text-sm font-medium text-zinc-300">Modus</p>
          <div className="grid grid-cols-1 gap-2">
            <button
              type="button"
              onClick={() => setPickedMode('soft')}
              className={`rounded-lg border px-3 py-3 text-left text-sm transition-colors ${
                pickedMode === 'soft'
                  ? 'border-amber-500 bg-amber-700/30 text-amber-100'
                  : 'border-zinc-700 bg-zinc-900 text-zinc-300 hover:border-zinc-500'
              }`}
            >
              <div className="font-semibold">Weich</div>
              <div className="mt-0.5 text-xs text-zinc-400">
                Markierung an der Tafel — kein Auto-DQ.
              </div>
            </button>
            <button
              type="button"
              onClick={() => setPickedMode('hard')}
              className={`rounded-lg border px-3 py-3 text-left text-sm transition-colors ${
                pickedMode === 'hard'
                  ? 'border-red-500 bg-red-700/30 text-red-100'
                  : 'border-zinc-700 bg-zinc-900 text-zinc-300 hover:border-zinc-500'
              }`}
            >
              <div className="font-semibold">Hart, offen</div>
              <div className="mt-0.5 text-xs text-zinc-400">
                Auto-DQ bei Überschreiten — Timer zeichnet bis Schusszahl weiter auf, DQ aufhebbar.
              </div>
            </button>
            <button
              type="button"
              onClick={() => setPickedMode('hard_stop')}
              className={`rounded-lg border px-3 py-3 text-left text-sm transition-colors ${
                pickedMode === 'hard_stop'
                  ? 'border-red-500 bg-red-700/30 text-red-100'
                  : 'border-zinc-700 bg-zinc-900 text-zinc-300 hover:border-zinc-500'
              }`}
            >
              <div className="font-semibold">Hart, Stopp</div>
              <div className="mt-0.5 text-xs text-zinc-400">
                Auto-DQ bei Überschreiten — Timer stoppt sofort, DQ endgültig.
              </div>
            </button>
          </div>
        </div>
      )}

      <div className="pt-2">
        <button
          type="button"
          onClick={() => void start()}
          disabled={!canStart || busy}
          className="w-full rounded-lg bg-emerald-700 px-4 py-4 text-lg font-bold text-white transition-colors hover:bg-emerald-600 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {busy
            ? '⏳ Starte…'
            : selection.kind === 'none'
              ? 'Starten — ohne PAR'
              : `Starten — ${effectiveS != null ? fmtSeconds(effectiveS) : '—'} (${pickedMode === 'soft' ? 'weich' : pickedMode === 'hard_stop' ? 'hart, Stopp' : 'hart, offen'})`}
        </button>
      </div>
    </section>
  );
}
